import * as React from 'react'
import { differenceInCalendarDays, format } from 'date-fns'
import DashboardCard from '@/pages/dashboard/DashboardCard'
import { Skeleton } from '@/components/ui/skeleton'
import useAppStore from '@/store/useAppStore'
import useGetCashflowPeriods from '@/services/api/cash-flow/useGetCashflowPeriods'
import useVaultSelection from '@/pages/dashboard/useVaultSelection'
import { swatchColor } from '@/pages/dashboard/vaultColor'
import {
  buildTotalSeries,
  buildVaultSeries,
  splitActualForecast,
  findLowPoint,
  computeYAxis,
  toPath,
  xForOffsetIndex,
  yForValue,
  type ChartPeriod,
} from '@/pages/dashboard/chartMath'

const WIDTH = 1000
const HEIGHT = 260

function formatCompact(value: number): string {
  const sign = value < 0 ? '-' : ''
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}m`
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}k`
  return `${sign}$${Math.round(abs)}`
}

export default function ProjectedBalanceCard() {
  const offset = useAppStore((state) => state.offset)
  const numberOfPeriods = useAppStore((state) => state.numberOfPeriods)
  const periodType = useAppStore((state) => state.periodType)
  // Set by the Status banner's breach link (FE 19) to point at the specific
  // period it's talking about.
  const highlightedPeriodOffset = useAppStore((state) => state.highlightedPeriodOffset)

  const { data: periods, status } = useGetCashflowPeriods({
    offset,
    numberOfPeriods,
    periodType,
  })

  const {
    vaultsFlat,
    selectedVaultIds,
    selectedVaults,
    toggleVault,
    isSelected,
    splitByVault,
  } = useVaultSelection()

  const chart = React.useMemo(() => {
    const columns = ((periods ?? []) as ChartPeriod[])
      .slice()
      .sort((a, b) => a.offset - b.offset)
    if (columns.length === 0) return null

    const allOffsets = columns.map((c) => c.offset)
    const allVaultIds = vaultsFlat.map((v) => v.id)

    const totalSeries = buildTotalSeries(columns, allVaultIds, selectedVaultIds)
    const total = splitActualForecast(totalSeries)

    const vaultLines = splitByVault
      ? selectedVaults.map((vault) => {
          const series = buildVaultSeries(columns, vault.id)
          return { vault, ...splitActualForecast(series) }
        })
      : []

    const valuesForScale = [
      ...totalSeries.map((p) => p.value),
      ...vaultLines.flatMap((l) => [...l.solid, ...l.dashed].map((p) => p.value)),
    ]
    const scale = computeYAxis(valuesForScale)

    const lowPoint = findLowPoint(totalSeries)
    const lowPointColumn = lowPoint
      ? columns.find((c) => c.offset === lowPoint.offset)
      : null

    // The forecast region starts at the boundary between the last actual
    // period (offset -1) and the current period (offset 0) — a column edge,
    // never a `today` comparison.
    const firstForecastIndex = allOffsets.findIndex((o) => o >= 0)
    const forecastX =
      firstForecastIndex === -1
        ? WIDTH
        : xForOffsetIndex(
            Math.max(firstForecastIndex - 0.5, 0),
            columns.length,
            WIDTH,
          )

    // "Today" sits *inside* the current period, not on its edge — placed
    // proportionally through the column so it lands on the real date. The
    // forecast shading above deliberately starts at the column boundary
    // instead, so the two markers answer different questions and are drawn
    // differently.
    const currentIndex = allOffsets.findIndex((o) => o === 0)
    let todayX: number | null = null
    if (currentIndex !== -1) {
      const column = columns[currentIndex]
      const spanDays =
        differenceInCalendarDays(new Date(column.end), new Date(column.start)) + 1
      const elapsedDays =
        differenceInCalendarDays(new Date(), new Date(column.start)) + 1
      const fraction =
        spanDays > 0 ? Math.min(Math.max(elapsedDays / spanDays, 0), 1) : 0
      todayX = xForOffsetIndex(
        currentIndex - 0.5 + fraction,
        columns.length,
        WIDTH,
      )
    }

    // Evenly-spaced x-axis labels, capped so they don't crowd narrow cards.
    const maxLabels = 7
    const labelStep = Math.max(1, Math.ceil(columns.length / maxLabels))
    const xLabels = columns
      .map((c, i) => ({ i, c }))
      .filter(({ i }) => i % labelStep === 0)
      .map(({ i, c }) => ({
        x: xForOffsetIndex(i, columns.length, WIDTH),
        label: format(new Date(c.start), periodType === 'month' ? 'MMM' : 'd MMM'),
      }))

    return {
      columns,
      allOffsets,
      total,
      vaultLines,
      scale,
      lowPoint,
      lowPointDate: lowPointColumn ? format(new Date(lowPointColumn.start), 'd MMM') : null,
      forecastX,
      todayX,
      xLabels,
    }
  }, [periods, vaultsFlat, selectedVaultIds, selectedVaults, splitByVault, periodType])

  return (
    <DashboardCard
      title="Projected balance"
      headerRight={
        <span className="text-xs text-txt-secondary">
          solid = actual · dashed = forecast
        </span>
      }
    >
      {status === 'pending' && <Skeleton className="h-[220px] w-full" />}

      {status === 'error' && (
        <div className="flex h-[220px] items-center justify-center text-sm text-txt-secondary">
          Couldn&apos;t load the projected balance.
        </div>
      )}

      {status === 'success' && chart && (
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <div
              className="flex shrink-0 flex-col justify-between py-1 text-right text-[11px] text-txt-secondary"
              style={{ width: 44, height: HEIGHT }}
            >
              {chart.scale.ticks
                .slice()
                .reverse()
                .map((tick) => (
                  <div key={tick}>{formatCompact(tick)}</div>
                ))}
            </div>
            <div className="min-w-0 flex-1">
              <svg
                viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                preserveAspectRatio="none"
                style={{ width: '100%', height: HEIGHT, display: 'block' }}
              >
                {/* Forecast shading goes down first so the gridlines below
                    stay legible across it — drawn over the top, its 0.9 opacity
                    washed them out and the future half of the chart lost its
                    horizontal reference entirely. */}
                <rect
                  x={chart.forecastX}
                  y={0}
                  width={Math.max(WIDTH - chart.forecastX, 0)}
                  height={HEIGHT}
                  fill="hsl(210,40%,98%)"
                  opacity={0.9}
                />

                {chart.scale.ticks.map((tick) => (
                  <line
                    key={tick}
                    x1={0}
                    y1={yForValue(tick, chart.scale, HEIGHT)}
                    x2={WIDTH}
                    y2={yForValue(tick, chart.scale, HEIGHT)}
                    stroke="hsl(214,32%,91%)"
                    strokeWidth={1}
                    vectorEffect="non-scaling-stroke"
                  />
                ))}

                {chart.todayX !== null && (
                  <line
                    x1={chart.todayX}
                    y1={0}
                    x2={chart.todayX}
                    y2={HEIGHT}
                    stroke="hsl(215,19%,65%)"
                    strokeWidth={1}
                    strokeDasharray="4 4"
                    vectorEffect="non-scaling-stroke"
                  />
                )}

                {chart.vaultLines.map(({ vault, solid, dashed }) => (
                  <g key={vault.id}>
                    <path
                      d={toPath(solid, chart.allOffsets, chart.scale, WIDTH, HEIGHT)}
                      fill="none"
                      stroke={swatchColor(vault.id)}
                      strokeWidth={1.6}
                      vectorEffect="non-scaling-stroke"
                    />
                    <path
                      d={toPath(dashed, chart.allOffsets, chart.scale, WIDTH, HEIGHT)}
                      fill="none"
                      stroke={swatchColor(vault.id)}
                      strokeWidth={1.6}
                      strokeDasharray="5 4"
                      vectorEffect="non-scaling-stroke"
                    />
                  </g>
                ))}

                <path
                  d={toPath(chart.total.solid, chart.allOffsets, chart.scale, WIDTH, HEIGHT)}
                  fill="none"
                  stroke="hsl(212,92%,45%)"
                  strokeWidth={2.5}
                  vectorEffect="non-scaling-stroke"
                />
                <path
                  d={toPath(chart.total.dashed, chart.allOffsets, chart.scale, WIDTH, HEIGHT)}
                  fill="none"
                  stroke="hsl(212,92%,45%)"
                  strokeWidth={2.5}
                  strokeDasharray="6 5"
                  vectorEffect="non-scaling-stroke"
                />

                {chart.lowPoint && (
                  <circle
                    cx={xForOffsetIndex(
                      chart.allOffsets.indexOf(chart.lowPoint.offset),
                      chart.columns.length,
                      WIDTH,
                    )}
                    cy={yForValue(chart.lowPoint.value, chart.scale, HEIGHT)}
                    r={4}
                    fill="hsl(356,72%,47%)"
                  />
                )}

                {highlightedPeriodOffset !== null &&
                  chart.allOffsets.includes(highlightedPeriodOffset) && (
                    <line
                      x1={xForOffsetIndex(
                        chart.allOffsets.indexOf(highlightedPeriodOffset),
                        chart.columns.length,
                        WIDTH,
                      )}
                      y1={0}
                      x2={xForOffsetIndex(
                        chart.allOffsets.indexOf(highlightedPeriodOffset),
                        chart.columns.length,
                        WIDTH,
                      )}
                      y2={HEIGHT}
                      stroke="hsl(356,72%,47%)"
                      strokeWidth={2}
                      strokeDasharray="4 3"
                      vectorEffect="non-scaling-stroke"
                    />
                  )}
              </svg>

              <div className="flex justify-between text-[11px] text-txt-secondary">
                {chart.xLabels.map((l) => (
                  <div key={l.x}>{l.label}</div>
                ))}
              </div>
            </div>
          </div>

          {chart.lowPoint && (
            <div className="text-xs text-txt-secondary">
              Low point{' '}
              <span className="font-semibold text-[hsl(356,72%,47%)]">
                {formatCompact(chart.lowPoint.value)}
              </span>
              {chart.lowPointDate && <> · {chart.lowPointDate}</>}
            </div>
          )}

          <div className="pf-hscroll flex gap-3 overflow-x-auto border-t border-light pt-2">
            {vaultsFlat.map((vault) => {
              const lastColumn = chart.columns[chart.columns.length - 1]
              const currentBalance = lastColumn?.vaults?.[vault.id]?.closing_balance
              const selected = isSelected(vault.id)
              return (
                <button
                  key={vault.id}
                  type="button"
                  onClick={() => toggleVault(vault.id, !selected)}
                  className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-1.5 py-1 text-xs ${
                    selected ? 'opacity-100' : 'opacity-40'
                  }`}
                >
                  <span
                    className="h-[3px] w-3.5 shrink-0 rounded-sm"
                    style={{ background: swatchColor(vault.id) }}
                  />
                  <span className="font-medium text-txt-primary">{vault.name}</span>
                  {currentBalance !== undefined && (
                    <span className="tabular-nums text-txt-secondary">
                      {formatCompact(currentBalance)}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </DashboardCard>
  )
}
